(() => {
  'use strict';

  /*
   * Yu-Yulino approximate local currency
   *
   * Base/store currency remains EUR.
   * Checkout remains unchanged.
   * This script only displays an approximate
   * local equivalent to the visitor.
   */

  if (window.YYLocalCurrency?.initialized) {
    return;
  }

  const CARD_SELECTOR =
    '[data-yy-card-local-price]';

  const PRODUCT_SELECTOR =
    '[data-yy-product-local-price]';

  const TARGET_SELECTOR =
    `${CARD_SELECTOR}, ${PRODUCT_SELECTOR}`;


  /*
   * Country -> informational local currency
   *
   * EUR countries are intentionally absent.
   */

  const COUNTRY_TO_CURRENCY = {
    GE: 'GEL',

    GB: 'GBP',
    CH: 'CHF',
    NO: 'NOK',
    SE: 'SEK',
    DK: 'DKK',
    PL: 'PLN',
    CZ: 'CZK',
    HU: 'HUF',
    RO: 'RON',

    AU: 'AUD',
    NZ: 'NZD',

    US: 'USD',
    CA: 'CAD'
  };


  /*
   * Same FX source family already used
   * by the existing Yu-Yulino converter.
   *
   * The second URL is only used if
   * the first one fails.
   */

  const FX_URLS = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.min.json',
    'https://latest.currency-api.pages.dev/v1/currencies/eur.min.json'
  ];


  /*
   * Cache the EUR -> local rate
   * for 12 hours.
   */

  const CACHE_TTL =
    12 * 60 * 60 * 1000;


  /*
   * Product-page variant JSON is cached
   * per product-price element.
   */

  const variantsCache =
    new WeakMap();


  let contextPromise = null;
  let observer = null;
  let queuedFrame = null;


  /*
   * Number formatting.
   *
   * Currency symbols are handled separately
   * because AUD/NZD/CAD need unambiguous
   * prefixes such as A$, NZ$, C$.
   */

  const numberFormatter =
    new Intl.NumberFormat(
      undefined,
      {
        maximumFractionDigits: 0
      }
    );


  function getCountry() {
    return String(
      window
        .YY_LOCAL_CURRENCY_CONFIG
        ?.country || ''
    )
      .trim()
      .toUpperCase();
  }


  function getCurrency(country) {
    return (
      COUNTRY_TO_CURRENCY[country] ||
      null
    );
  }


  function getCacheKey(currency) {
    /*
     * Keep the existing cache namespace
     * used by the current product converter.
     */

    return `yy_fx_eur_${currency}_v3`;
  }


  function readCache(currency) {
    try {
      const raw =
        localStorage.getItem(
          getCacheKey(currency)
        );

      if (!raw) {
        return null;
      }

      const parsed =
        JSON.parse(raw);

      const rate =
        Number(parsed.rate);

      const savedAt =
        Number(parsed.savedAt || 0);

      if (
        !Number.isFinite(rate) ||
        rate <= 0 ||
        !savedAt
      ) {
        return null;
      }

      if (
        Date.now() - savedAt >=
        CACHE_TTL
      ) {
        return null;
      }

      return rate;

    } catch (error) {
      return null;
    }
  }


  function writeCache(
    currency,
    rate
  ) {
    try {
      localStorage.setItem(
        getCacheKey(currency),
        JSON.stringify({
          rate,
          savedAt: Date.now()
        })
      );

    } catch (error) {
      /*
       * localStorage may be unavailable
       * in some privacy modes.
       * The converter simply continues
       * without caching.
       */
    }
  }


  async function fetchRateFrom(
    url,
    currency
  ) {
    const response =
      await fetch(
        url,
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        `FX request failed: ${response.status}`
      );
    }

    const data =
      await response.json();

    const rate =
      Number(
        data
          ?.eur
          ?.[currency.toLowerCase()]
      );

    if (
      !Number.isFinite(rate) ||
      rate <= 0
    ) {
      throw new Error(
        'Invalid FX rate'
      );
    }

    return rate;
  }


  async function fetchRate(
    currency
  ) {
    for (const url of FX_URLS) {
      try {
        return await fetchRateFrom(
          url,
          currency
        );

      } catch (error) {
        /*
         * Try the secondary endpoint.
         */
      }
    }

    throw new Error(
      'Unable to load FX rate'
    );
  }


  /*
   * Creates ONE shared promise for
   * the whole page.
   *
   * Every product card uses this
   * same result.
   */

  function getContext() {
    if (contextPromise) {
      return contextPromise;
    }

    const country =
      getCountry();

    const currency =
      getCurrency(country);


    /*
     * Includes:
     * - EUR countries
     * - unsupported countries
     * - failed country detection
     *
     * Nothing is shown in these cases.
     */

    if (
      !country ||
      !currency
    ) {
      contextPromise =
        Promise.resolve(null);

      return contextPromise;
    }


    contextPromise =
      (async () => {

        const cachedRate =
          readCache(currency);

        if (cachedRate) {
          return {
            country,
            currency,
            rate: cachedRate
          };
        }


        try {
          /*
           * One FX rate for the whole page.
           */

          const rate =
            await fetchRate(currency);

          writeCache(
            currency,
            rate
          );

          return {
            country,
            currency,
            rate
          };

        } catch (error) {
          /*
           * Requirement:
           * fail silently.
           */

          return null;
        }

      })();


    return contextPromise;
  }


  function formatNumber(
    value
  ) {
    return numberFormatter.format(
      Math.round(value)
    );
  }


  function formatMoney(
    value,
    currency
  ) {
    const number =
      formatNumber(value);


    switch (currency) {

      case 'GBP':
        return `£${number}`;

      case 'CHF':
        return `CHF ${number}`;

      case 'NOK':
        return `${number} kr`;

      case 'SEK':
        return `${number} kr`;

      case 'DKK':
        return `${number} kr`;

      case 'PLN':
        return `${number} zł`;

      case 'CZK':
        return `${number} Kč`;

      case 'HUF':
        return `${number} Ft`;

      case 'RON':
        return `${number} lei`;

      case 'AUD':
        return `A$${number}`;

      case 'NZD':
        return `NZ$${number}`;

      case 'USD':
        return `$${number}`;

      case 'CAD':
        return `C$${number}`;

      case 'GEL':
        return `${number} ₾`;

      default:
        return `${number} ${currency}`;
    }
  }


  function convertCents(
    cents,
    context
  ) {
    const eur =
      cents / 100;

    const converted =
      eur * context.rate;

    return formatMoney(
      converted,
      context.currency
    );
  }


  /*
   * ---------------------------------------
   * PRODUCT CARDS
   * ---------------------------------------
   */

  function hideCardPrice(
    element
  ) {
    element.textContent = '';
    element.hidden = true;
  }


  function renderCardPrice(
    element,
    context
  ) {
    const cents =
      Number(
        element.dataset.eurCents
      );

    if (
      !context ||
      !Number.isFinite(cents) ||
      cents < 0
    ) {
      hideCardPrice(element);
      return;
    }


    const formatted =
      convertCents(
        cents,
        context
      );


    const varies =
      element.dataset
        .priceVaries === 'true';


    /*
     * A product with different
     * variant prices mirrors Shopify's
     * own "From" logic.
     */

    element.textContent =
      varies
        ? `≈ from ${formatted}`
        : `≈ ${formatted}`;


    element.hidden = false;
  }


  /*
   * ---------------------------------------
   * PRODUCT PAGE
   * ---------------------------------------
   */

  function getProductVariants(
    root
  ) {
    if (
      variantsCache.has(root)
    ) {
      return variantsCache.get(
        root
      );
    }


    let variants = [];


    const siblingScript =
      root.nextElementSibling;


    let script = null;


    if (
      siblingScript?.matches?.(
        '[data-yy-local-price-variants]'
      )
    ) {
      script = siblingScript;
    }


    if (!script) {
      script =
        root
          .parentElement
          ?.querySelector(
            '[data-yy-local-price-variants]'
          ) ||
        root
          .closest(
            'product-info'
          )
          ?.querySelector(
            '[data-yy-local-price-variants]'
          );
    }


    try {
      variants =
        script
          ? JSON.parse(
              script.textContent
            )
          : [];

      if (
        !Array.isArray(
          variants
        )
      ) {
        variants = [];
      }

    } catch (error) {
      variants = [];
    }


    variantsCache.set(
      root,
      variants
    );


    return variants;
  }


  function findVariant(
    variants,
    id
  ) {
    return variants.find(
      variant =>
        String(variant.id) ===
        String(id)
    );
  }


  function getSelectedVariant(
    root,
    variants
  ) {
    const productInfo =
      root.closest(
        'product-info'
      );


    const scope =
      productInfo ||
      root.closest(
        '.shopify-section'
      ) ||
      document;


    /*
     * Craft updates hidden
     * variant IDs in product forms.
     */

    const inputs =
      scope.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"]'
      );


    for (
      const input of inputs
    ) {
      const variant =
        findVariant(
          variants,
          input.value
        );

      if (variant) {
        return variant;
      }
    }


    /*
     * Fallback to ?variant=
     */

    const urlVariant =
      new URLSearchParams(
        window.location.search
      ).get('variant');


    if (urlVariant) {
      const variant =
        findVariant(
          variants,
          urlVariant
        );

      if (variant) {
        return variant;
      }
    }


    /*
     * Initial server-rendered variant.
     */

    return findVariant(
      variants,
      root.dataset.variantId
    );
  }


  function syncProductVariant(
    root
  ) {
    const variants =
      getProductVariants(root);


    if (!variants.length) {
      return;
    }


    const variant =
      getSelectedVariant(
        root,
        variants
      );


    if (!variant) {
      return;
    }


    root.dataset.variantId =
      String(variant.id);


    root.dataset.eurCents =
      String(variant.price);
  }


  function renderProductPrice(
    root,
    context
  ) {
    syncProductVariant(root);


    const amountNode =
      root.querySelector(
        '[data-yy-local-amount]'
      );


    const cents =
      Number(
        root.dataset.eurCents
      );


    if (
      !context ||
      !amountNode ||
      !Number.isFinite(cents) ||
      cents < 0
    ) {
      root.hidden = true;
      return;
    }


    const formatted =
      convertCents(
        cents,
        context
      );


    amountNode.textContent =
      `Approx. ${formatted} in your currency`;


    root.hidden = false;
  }


  /*
   * ---------------------------------------
   * FIND AND RENDER TARGETS
   * ---------------------------------------
   */

  function collectTargets(
    root,
    selector
  ) {
    const result = [];


    if (
      root instanceof Element &&
      root.matches(selector)
    ) {
      result.push(root);
    }


    if (
      root &&
      typeof root.querySelectorAll ===
        'function'
    ) {
      result.push(
        ...root.querySelectorAll(
          selector
        )
      );
    }


    return result;
  }


  function scan(
    root = document
  ) {
    const cards =
      collectTargets(
        root,
        CARD_SELECTOR
      );


    const productPrices =
      collectTargets(
        root,
        PRODUCT_SELECTOR
      );


    if (
      !cards.length &&
      !productPrices.length
    ) {
      return;
    }


    /*
     * ALL targets share the same
     * page-level contextPromise.
     */

    getContext().then(
      context => {

        cards.forEach(
          element =>
            renderCardPrice(
              element,
              context
            )
        );


        productPrices.forEach(
          element =>
            renderProductPrice(
              element,
              context
            )
        );

      }
    );
  }


  /*
   * ---------------------------------------
   * DYNAMIC SHOPIFY SECTIONS
   * ---------------------------------------
   *
   * Collection filters/sorting replace
   * ProductGridContainer.
   *
   * Recommendations are loaded later.
   *
   * Therefore new cards must be detected
   * after initial page load.
   */

  function containsTarget(
    node
  ) {
    if (
      !(
        node instanceof Element ||
        node instanceof DocumentFragment
      )
    ) {
      return false;
    }


    if (
      node instanceof Element &&
      node.matches(
        TARGET_SELECTOR
      )
    ) {
      return true;
    }


    return Boolean(
      node.querySelector?.(
        TARGET_SELECTOR
      )
    );
  }


  function queueScan(
    root = document
  ) {
    if (queuedFrame) {
      return;
    }


    queuedFrame =
      requestAnimationFrame(
        () => {
          queuedFrame = null;

          scan(root);
        }
      );
  }


  function startObserver() {
    if (
      observer ||
      !document.body
    ) {
      return;
    }


    observer =
      new MutationObserver(
        mutations => {

          for (
            const mutation
            of mutations
          ) {

            for (
              const node
              of mutation.addedNodes
            ) {

              if (
                containsTarget(node)
              ) {
                queueScan(
                  document
                );

                return;
              }
            }
          }
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /*
   * Product variant change fallback.
   */

  function handleProductChange(
    event
  ) {
    const productInfo =
      event.target?.closest?.(
        'product-info'
      );


    if (!productInfo) {
      return;
    }


    /*
     * First pass:
     * immediate form update.
     */

    setTimeout(
      () => scan(productInfo),
      0
    );


    /*
     * Second pass:
     * after Craft's Section Rendering
     * finishes updating the variant.
     */

    setTimeout(
      () => scan(productInfo),
      150
    );
  }


  function init() {
    scan(document);

    startObserver();


    document.addEventListener(
      'change',
      handleProductChange
    );


    /*
     * Shopify theme editor /
     * section re-render.
     */

    document.addEventListener(
      'shopify:section:load',
      event => {
        scan(event.target);
      }
    );


    /*
     * Native Craft/Dawn pub-sub
     * variant event, when available.
     */

    if (
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(
        PUB_SUB_EVENTS.variantChange,
        () => {
          queueScan(document);
        }
      );
    }
  }


  /*
   * Public refresh method if another
   * custom section ever needs it.
   */

  window.YYLocalCurrency = {
    initialized: true,

    refresh(
      root = document
    ) {
      scan(root);
    }
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {
    init();
  }

})();